/**
 * Every word the session drag says — the grip's name, the seven menu entries, and the sentence the
 * live region reads after a drop.
 *
 * The mechanic itself lives in `entities/plan-draft`: `moveCommands` decides what a drop means and
 * `dropGates` decides whether it may happen at all. Both answer in FACTS — a weekday, a position, a
 * refusal code — because `model/` in an entity holds no sentences. This is where those facts become
 * Polish, and it is a separate module from `weekViewModel` for one reason: the announcements are
 * produced at the moment of a drop, not at render, so they are not part of the view model at all.
 *
 * ── The refusal wording is shared with nothing, on purpose ───────────────────────────────────
 * `DropRefusal` is a five-member union and every member is worded here. The dock words the same
 * union again for the exercise mechanics, and that duplication is deliberate: "ta faza jest
 * zamknięta" is the same fact about two different things, and a shared string would have to be
 * vague enough to cover a session card and an exercise row at once.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type { DropRefusal, MoveOutcome } from '~/entities/plan-draft';

import type { Translate } from './builderCopy';
import type { MoveTargetView } from './dayCardView';

const KEY = 'web:planBuilder.dnd';

/**
 * The seven weekdays, as move targets for one day.
 *
 * `occupants` is the phase's other programmed days by weekday, which is what turns "Środa" into
 * "Środa — Pull A, zamiana". A trainer who cannot see that the target is taken would read a swap as
 * their other workout vanishing.
 *
 * The `coordinate` a stop carries is the WEEKDAY, 1–7, which is what `moveDayToWeekday` takes. The
 * tape's `positionTargets` fills the same field with a 0-based position; between the two of them
 * nothing downstream has to know which, and that is what lets one card component serve both.
 */
export function weekdayTargets(
  t: Translate,
  options: {
    readonly weekdayLabels: readonly string[];
    readonly currentWeekday: number | null;
    /** Weekday → the session name standing there, for every weekday another day of the phase holds. */
    readonly occupants: ReadonlyMap<number, string>;
  },
): MoveTargetView[] {
  return [1, 2, 3, 4, 5, 6, 7].map((weekday) => {
    const label = options.weekdayLabels[weekday - 1] ?? '';
    const occupant = options.occupants.get(weekday);
    if (weekday === options.currentWeekday) {
      return {
        coordinate: weekday,
        label: t(`${KEY}.weekdayCurrent`, { weekday: label }),
        disabledReason: t(`${KEY}.weekdayCurrentReason`),
        preview: t(`${KEY}.weekdayCurrent`, { weekday: label }),
      };
    }
    return {
      coordinate: weekday,
      label:
        occupant === undefined
          ? t(`${KEY}.weekdayFree`, { weekday: label })
          : t(`${KEY}.weekdayOccupied`, { weekday: label, name: occupant }),
      disabledReason: null,
      preview:
        occupant === undefined
          ? t(`${KEY}.dayPreviewFree`, { weekday: label })
          : t(`${KEY}.dayPreviewOccupied`, { weekday: label, name: occupant }),
    };
  });
}

export function refusalWords(t: Translate, reason: DropRefusal): string {
  return t(`${KEY}.refusal.${reason}`);
}

/**
 * The weekday in the ACCUSATIVE — the form Polish requires after "na", and the only place a
 * weekday name is not simply what `Intl` hands over.
 *
 * "Przeniesiono Push A na środa" is what the nominative produces and it is wrong; the sentence
 * needs "na środę". Three of the seven inflect — środa/sobota/niedziela — and the four masculine
 * ones do not, which is exactly why the defect survived: a drop onto Monday, Tuesday, Thursday or
 * Friday reads perfectly.
 *
 * It is a TABLE rather than a call into `Intl` because no `Intl` API exposes grammatical case;
 * `DateTimeFormat` and `DisplayNames` both answer in the nominative and nothing else is offered.
 * That makes this the one weekday string this app cannot ask the platform for — and it is a
 * different thing from the mobile app's static date tables, which exist because Hermes throws.
 *
 * Only the "na" sentences use it. The menu and the previews name a weekday as a subject —
 * "Środa — wolne" — and take the nominative `Intl` already gives them.
 */
function weekdayAccusative(t: Translate, weekday: number): string {
  return t(`web:weekdays.accusative.${String(weekday)}`);
}

/**
 * What the live region says once a session move has been dispatched.
 *
 * Every branch of `MoveOutcome` that this mechanic can produce is worded, including `unchanged` —
 * silence after a deliberate drop reads as a broken control, and a trainer who dropped a card back
 * where it started needs to be told nothing happened rather than left to check.
 */
export function dayMoveAnnouncement(
  t: Translate,
  outcome: MoveOutcome,
  context: {
    readonly sessionName: string;
    readonly weekdayLabels: readonly string[];
    readonly currentWeekday: number | null;
  },
): string {
  if (outcome.kind === 'refused') return refusalWords(t, outcome.reason satisfies DropRefusal);
  if (outcome.kind === 'unchanged') {
    return t(`${KEY}.dayUnchanged`, {
      name: context.sessionName,
      weekday: context.weekdayLabels[(context.currentWeekday ?? 1) - 1] ?? '',
    });
  }
  if (outcome.kind !== 'movedDay') return '';
  // Both sentences read "… na {{weekday}}", so both take the accusative. `weekdayLabels` is not
  // consulted here at all — it carries `Intl`'s nominative, which is the wrong case in this frame.
  const weekday = weekdayAccusative(t, outcome.weekday);
  return outcome.swappedWith === null
    ? t(`${KEY}.dayMoved`, { name: context.sessionName, weekday })
    : t(`${KEY}.dayMovedSwap`, {
        name: context.sessionName,
        weekday,
        other: outcome.swappedWith,
      });
}
